import { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Button, Icon, Select, type Column, SearchBar } from "@legal-500/legal500-design-system";
import { PageHeader } from "@/components/PageHeader/PageHeader.js";
import LoadingState from "@/components/LoadingState/index.js";
import { useQuery } from "@/hooks/useApi.js";
import { type ActivityLogEntry, docParsing } from "@/api/docParsing.service.js";
import styles from "@/pages/Submissions/ParsingJobs/ParsingJobs.module.css";
import monitoringStyles from "./ParseMonitoring.module.css";
import { DataTable } from "@/components/DataTable/DataTable.js";
import EmptyState from "@/components/EmptyState/index.js";
import { PaginationControls } from "@/components/PaginationControls/PaginationControls.js";

const DEFAULT_LOG_PAGE_SIZE = 20;

type ActivityLogProps = {
  sharepointFileId: string | null;
};

function ActivityLogSection({ sharepointFileId }: ActivityLogProps) {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_LOG_PAGE_SIZE);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const { data, loading, error } = useQuery(
    () =>
      sharepointFileId
        ? docParsing.listActivityLogs({
            resourceType: "doc-parsing-file",
            resourceId: sharepointFileId,
            search: debouncedSearch || undefined,
            limit: pageSize,
            offset: (page - 1) * pageSize,
          })
        : Promise.resolve({
            results: [],
            pagination: { total: 0, limit: pageSize, offset: 0 },
            requestId: "",
          }),
    [sharepointFileId, debouncedSearch, page, pageSize],
  );

  const logs = data?.results ?? [];
  const total = data?.pagination.total ?? 0;
  const totalPages = Math.ceil(total / pageSize);
  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + logs.length;

  const columns: Column<ActivityLogEntry>[] = useMemo(
    () => [
      {
        header: "Time",
        accessor: "createdAt" as keyof ActivityLogEntry,
        render: (value: unknown) => new Date(value as string).toLocaleString(),
      },
      {
        header: "Action",
        accessor: "action" as keyof ActivityLogEntry,
        render: (value: unknown) => (
          <span className={monitoringStyles.actionBadge}>{String(value)}</span>
        ),
      },
      {
        header: "Resource ID",
        accessor: "resourceId" as keyof ActivityLogEntry,
        render: (value: unknown) => {
          const id = String(value);
          return (
            <span
              className={monitoringStyles.copyableId}
              onClick={() => navigator.clipboard.writeText(id)}
            >
              {id}
            </span>
          );
        },
      },
      {
        header: "Actor",
        accessor: "actorId" as keyof ActivityLogEntry,
        render: (value: unknown, row: ActivityLogEntry) => {
          const actorId = String(value ?? "");
          const truncated = actorId.length > 12 ? `${actorId.slice(0, 12)}...` : actorId;
          return `${truncated} (${row.actorRole ?? "unknown"})`;
        },
      },
      {
        header: "Details",
        accessor: "details" as keyof ActivityLogEntry,
        render: (value: unknown) => {
          const details = value as Record<string, unknown> | null;
          if (!details || Object.keys(details).length === 0) return "—";
          return <span className={monitoringStyles.detailsCell}>{JSON.stringify(details)}</span>;
        },
      },
    ],
    [],
  );

  return (
    <div>
      <h3 className={monitoringStyles.sectionTitle}>Activity Log</h3>
      <div className={monitoringStyles.searchBar}>
        <SearchBar
          value={search}
          onChange={setSearch}
          placeholder="Search by file name or ID..."
          onClear={() => setSearch("")}
        />
      </div>

      <div className={monitoringStyles.rowsPerPageControl}>
        <label htmlFor="logsPageSize" className={monitoringStyles.rowsPerPageLabel}>
          Rows per page:
        </label>
        <Select
          id="logsPageSize"
          value={pageSize}
          onChange={e => {
            setPageSize(Number(e.target.value));
            setPage(1);
          }}
          options={[
            { value: 10, label: "10" },
            { value: 20, label: "20" },
            { value: 50, label: "50" },
            { value: 100, label: "100" },
          ]}
          size="small"
        />
      </div>

      <div className={monitoringStyles.tableContainer}>
        {error && !loading && (
          <div className={styles.activityLogEmpty}>Failed to load activity logs.</div>
        )}

        {!error && loading && <LoadingState message="Loading activity logs..." />}

        {!error && !loading && logs.length === 0 && (
          <EmptyState
            variant="empty"
            title="No activity logs"
            message="No doc-parsing activity logs found."
          />
        )}

        {!error && !loading && logs.length > 0 && (
          <>
            <DataTable data={logs} columns={columns} rowKey="id" size="small" />
            <PaginationControls
              currentPage={page}
              totalPages={totalPages}
              startIndex={startIndex}
              endIndex={endIndex}
              totalCount={total}
              itemName="logs"
              onPageChange={setPage}
            />
          </>
        )}
      </div>
    </div>
  );
}

function ParseMonitoringDetail() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const routeFileName = (location.state as { fileName?: string } | null)?.fileName;

  const { data, loading, error, refetch } = useQuery(() => docParsing.getResultById(id!), [id]);

  const result = data?.result ?? null;
  const isActive = result?.status === "processing" || result?.status === "queued";

  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  useEffect(() => {
    if (!isActive) return;
    const interval = setInterval(() => refetchRef.current(), 5_000);
    return () => clearInterval(interval);
  }, [isActive]);

  const displayName = result?.fileName ?? routeFileName ?? "Parse Monitoring";

  return (
    <div className={styles.wrapper}>
      <PageHeader title={displayName} subtitle={id ?? ""}>
        <div style={{ display: "flex", gap: "12px" }}>
          <Button variant="outlined" color="default" onClick={() => navigate(-1)}>
            <Icon icon="arrow-left" size="small" />
            Back
          </Button>
        </div>
      </PageHeader>

      {loading && <LoadingState message="Loading parsing job..." />}

      {error && !loading && (
        <div style={{ color: "var(--color-danger)", padding: "1rem 0" }}>
          Failed to load parsing job: {error.message}
        </div>
      )}

      {result && !loading && (
        <div className={styles.detailContent}>
          <div className={styles.detailLeftColumn}>
            <ActivityLogSection sharepointFileId={result.sharepointFileId} />
          </div>
          <div className={styles.detailRightColumn}>
            <div className={styles.jsonCard}>
              <h3 className={styles.jsonTitle}>Parsed Result</h3>
              {result.parsedJson != null ? (
                <pre className={styles.jsonPre}>{JSON.stringify(result.parsedJson, null, 2)}</pre>
              ) : (
                <p className={styles.jsonEmpty}>No parsed data available</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ParseMonitoringDetail;
